var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CpslButton, CpslIcon, CpslQrCode, CpslSpinner, CpslText } from '@usecapsule/react-components';
import { CenteredText, InnerStepContainer, QRContainer, StepContainer } from '../common.js';
import { useEffect, useMemo } from 'react';
import { useModalStore } from '../../stores/index.js';
import styled from 'styled-components';
import { useExternalWallets } from '../../providers/ExternalWalletContext.js';
import { useCopyToClipboard } from '@usecapsule/react-common';
import { ModalStep } from '../../utils/steps.js';
import { isMobile, isTablet, WalletType } from '@usecapsule/web-sdk';
import { routeMobileExternalWallet } from '../../utils/routeMobileExternalWallet.js';
export const ExternalWalletStep = () => {
    const [isCopied, copy] = useCopyToClipboard();
    const externalWalletError = useModalStore(state => state.externalWalletError);
    const setStep = useModalStore(state => state.setStep);
    const { connectExternalWallet, wallet, qrUri, walletDisplayHelpers } = useExternalWallets();
    useEffect(() => {
        routeMobileExternalWallet(qrUri);
    }, [qrUri]);
    const handleTryAgainClick = () => __awaiter(void 0, void 0, void 0, function* () {
        yield connectExternalWallet(wallet);
    });
    const handleCopy = () => {
        copy(qrUri);
    };
    const Content = useMemo(() => {
        var _a, _b, _c;
        if (!wallet) {
            return null;
        }
        const isMobileWalletConnect = isMobile() && wallet.id === 'walletConnect';
        if (isMobileWalletConnect) {
            _jsx(InnerStepContainer, { children: _jsx(Text, { weight: "semiBold", children: "Continue in the WalletConnect modal." }) });
        }
        const { showExtension, showMobile, isSolanaMobileIOS } = walletDisplayHelpers;
        // Fallback to not supported text
        if ((!showMobile && !showExtension) || (isSolanaMobileIOS && !wallet.installed)) {
            const text = isSolanaMobileIOS
                ? "Solana wallets aren't available on mobile IOS browsers.\n\nPlease continue in the wallet app."
                : `${wallet.name} isn't supported on mobile devices.\n\nPlease choose another wallet or continue on desktop.`;
            return (_jsx(InnerStepContainer, { children: _jsx(Text, { weight: "semiBold", children: text }) }));
        }
        if (showExtension) {
            const isInstalled = wallet.installed;
            return (_jsxs(InnerStepContainer, { children: [isInstalled && !(externalWalletError === null || externalWalletError === void 0 ? void 0 : externalWalletError.length) ? (_jsx(CenteredText, { color: "contrast", weight: "semiBold", children: `Confirm connection request in the ${wallet.name} browser extension.` })) : (_jsxs(ErrorContainer, { children: [_jsx(ErrorIcon, { icon: "alertCircle" }), _jsx(CpslText, { weight: "semiBold", color: "error", children: isInstalled ? externalWalletError === null || externalWalletError === void 0 ? void 0 : externalWalletError[0] : `${wallet.name} not detected` })] })), _jsxs(CpslButton, { as: isInstalled ? 'button' : 'a', href: (_a = wallet.downloadUrl) !== null && _a !== void 0 ? _a : '', target: "_blank", variant: "secondary", onClick: handleTryAgainClick, children: [_jsx(CpslIcon, { slot: "start", icon: isInstalled ? 'refresh' : 'linkExternal' }), isInstalled ? 'Try Again' : `Get ${wallet.name}`] })] }));
        }
        if (showMobile) {
            // If Solana wallet or if on a mobile and NOT on a table, show the connection screen. Else show the QR code.
            if (wallet.type === WalletType.SOLANA || (isMobile() && !isTablet())) {
                // Checking if the wallet is installed only for Solana wallets since Solana MWA doesn't work on IOS Safari
                // https://docs.solanamobile.com/web/developing-for-web#ios-web
                const isInstalled = wallet.type !== WalletType.SOLANA || wallet.installed;
                return (_jsxs(_Fragment, { children: [_jsx(InnerStepContainer, { children: !isInstalled && (_jsx(CpslText, { weight: "semiBold", color: "error", children: `${wallet.name} not detected` })) }), wallet.id !== 'walletConnect' && (_jsxs(InnerStepContainer, { children: [_jsx(CpslButton, { onClick: () => routeMobileExternalWallet(qrUri), fullWidth: true, children: "Connect Wallet" }), _jsx(Link, { href: (_b = wallet.downloadUrl) !== null && _b !== void 0 ? _b : '', target: "_blank", children: _jsxs(ExternalButton, { children: [_jsx(ExternalText, { weight: "medium", children: `Get ${wallet.name}` }), _jsx(ExternalIcon, { icon: "linkExternal" })] }) })] }))] }));
            }
            return (_jsxs(_Fragment, { children: [_jsxs(InnerStepContainer, { children: [_jsx(CpslText, { weight: "semiBold", children: "Scan with your mobile device" }), _jsx(QRContainer, { children: !qrUri ? _jsx(CpslSpinner, { size: 100 }) : _jsx(CpslQrCode, { url: qrUri, imageSrc: wallet.iconUrl }) }), _jsxs(CpslButton, { size: "small", variant: "ghost", onClick: handleCopy, children: [_jsx(CpslIcon, { slot: "start", icon: isCopied ? 'check' : 'copy' }), isCopied ? 'Copied' : 'Copy Link'] })] }), _jsx(InnerStepContainer, { children: _jsx(Link, { href: (_c = wallet.downloadUrl) !== null && _c !== void 0 ? _c : '', target: "_blank", children: _jsxs(ExternalButton, { children: [_jsx(ExternalText, { weight: "medium", children: `Get ${wallet.name}` }), _jsx(ExternalIcon, { icon: "linkExternal" })] }) }) })] }));
        }
    }, [wallet, walletDisplayHelpers, externalWalletError, qrUri]);
    useEffect(() => {
        if (!wallet) {
            setStep(ModalStep.AUTH_MAIN);
        }
    }, [wallet]);
    if (!wallet) {
        return null;
    }
    return _jsx(Container, { children: Content });
};
const Container = styled(StepContainer) `
  flex: 1;
  justify-content: space-between;
`;
const ErrorContainer = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;
const ErrorIcon = styled(CpslIcon) `
  --height: 16px;
  --width: 16px;
  --icon-color: var(--cpsl-color-text-error);
`;
const Text = styled(CenteredText) `
  white-space: pre-line;
`;
const ExternalButton = styled.div `
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  width: 100%;
  cursor: pointer;
  margin-top: 8px;
  text-decoration: none;
`;
const ExternalIcon = styled(CpslIcon) `
  --height: 20px;
  --width: 20px;
`;
const ExternalText = styled(CpslText) `
  text-decoration: none;
`;
const Link = styled.a `
  text-decoration: none;
`;
